import React, { useCallback, useState, useEffect } from "react";
import moment from "moment";

import {
    surveyCard,
    questionTitle,
    surveyValue,
    surveyValueText,
    surveyCardEditing,
} from "./survey-answer-question-card.module.scss";
import { ISurveyQuestion } from "../../../models/survey/survey-question.model";
import SurveyAnswerQuestionCardEdit from "./survey-answer-question-card-edit";

export interface ISurveyAnswerQuestionCardProps {
    question: ISurveyQuestion;
    canEdit?: boolean;
    isEditing?: boolean;
    onEdit?(questionId: ISurveyQuestion["questionId"]): void;
    onSaveAnswer?(questionId: number, answer: string | number | Array<string | number>, shouldClose?: boolean): void;
    onStartEdit?(questionId: number): void;
    onCancelEdit?(): void;
}

export default function SurveyAnswerQuestionCard({
    question,
    onEdit,
    canEdit = true,
    onSaveAnswer,
    isEditing: isEditingProp = false,
    onStartEdit,
    onCancelEdit,
}: ISurveyAnswerQuestionCardProps) {
    const { options, title, answers, unit, questionId } = question;
    const [editValue, setEditValue] = useState<string | number | Array<string | number> | null>(
        answers || null
    );

    // Synchronizuj editValue z answers gdy się zmieniają
    // Ale tylko jeśli pytanie NIE jest w trakcie edycji (żeby nie resetować wartości podczas edycji)
    useEffect(() => {
        if (!isEditingProp) {
            setEditValue(answers || null);
        }
    }, [answers, isEditingProp]);

    const handleClick = useCallback(
        (e: React.MouseEvent) => {
            if (canEdit && !isEditingProp) {
                e.stopPropagation();
                if (onStartEdit) {
                    onStartEdit(questionId);
                }
                setEditValue(answers || null);
            }
        },
        [canEdit, isEditingProp, answers, questionId, onStartEdit]
    );

    const handleSave = useCallback((value?: string | number | Array<string | number>, shouldClose: boolean = true) => {
        const valueToSave = value !== undefined ? value : editValue;
        if (onSaveAnswer && valueToSave !== null && valueToSave !== undefined) {
            onSaveAnswer(questionId, valueToSave, shouldClose);
        }
        if (shouldClose && onCancelEdit) {
            onCancelEdit();
        }
    }, [onSaveAnswer, questionId, editValue, onCancelEdit]);

    const handleCancel = useCallback(() => {
        setEditValue(answers || null);
        if (onCancelEdit) {
            onCancelEdit();
        }
    }, [answers, onCancelEdit]);

    const handleChange = useCallback((value: string | number | Array<string | number>) => {
        setEditValue(value);
    }, []);

    const answersToDisplay = translateAnswers(answers, options, unit);

    return (
        <div
            className={`${surveyCard} ${isEditingProp ? surveyCardEditing : ""}`}
            onClick={canEdit && !isEditingProp ? handleClick : undefined}
            style={{ cursor: canEdit && !isEditingProp ? "pointer" : "default" }}
        >
            <h3 className={questionTitle}>{title}</h3>
            <div className={surveyValue}>
                {isEditingProp ? (
                    <SurveyAnswerQuestionCardEdit
                        question={question}
                        value={editValue}
                        onChange={handleChange}
                        onSave={handleSave}
                        onCancel={handleCancel}
                    />
                ) : (
                    <p className={surveyValueText}>{answersToDisplay || "Brak odpowiedzi"}</p>
                )}
            </div>
        </div>
    );
}

const translateAnswers = (
    answers: ISurveyQuestion["answers"],
    options: ISurveyQuestion["options"],
    unit: ISurveyQuestion["unit"]
) => {
    const processedAnswers = Array.isArray(answers) ? answers : [answers];

    if (!processedAnswers) {
        return "";
    }

    if (Array.isArray(options) && options.length > 0) {
        return processedAnswers
            .map((answer) => {
                const option = options.find((option) => {
                    const answerId = typeof answer === "string" ? parseInt(answer) : answer;
                    return option.id === answerId;
                });

                return option ? option.label : "";
            }, "")
            .join(", ");
    }

    return processedAnswers
        .map((answer) => {
            if (typeof answer === "string" && moment(answer, "YYYY-MM-DD", true).isValid()) {
                return moment(answer).format("DD.MM.YYYY");
            }
            return `${answer}${unit ? unit : ""}`;
        })
        .join(", ");
};
